import { utcToZonedTime } from "date-fns-tz";
import { z } from "zod";

import type { GetReviewCycleDetailsResponseFrontend } from "@performyard/service-contracts";

import { minimumEndDate } from "../../utils/automation-end-date";

import {
	AUTOMATION_TYPES,
	AutomationType,
	CYCLE_NAME_MAX_LENGTH,
} from "../../Create/ReviewCycleCreate/steps/CycleSettingsStep/constants";

// Cycle type as exposed by the review cycle details endpoint.
export type CycleType = "manual" | "automated" | "group";

const NAME_REQUIRED = "Review Cycle Name is required";
const START_DATE_REQUIRED = "Start Date is required";

const startOfDayInDays = (daysFromToday: number, timezone: string) => {
	const date = utcToZonedTime(new Date(), timezone);
	date.setDate(date.getDate() + daysFromToday);
	date.setHours(0, 0, 0, 0);
	return date;
};

const baseShape = {
	cycleType: z.enum(["manual", "automated", "group"]),
	name: z
		.string()
		.trim()
		.min(1, NAME_REQUIRED)
		.max(CYCLE_NAME_MAX_LENGTH, `Cycle name must be ${CYCLE_NAME_MAX_LENGTH} characters or fewer`),
	lockReviews: z.boolean(),
	startDate: z
		.date()
		.nullable()
		.refine((value) => value !== null, START_DATE_REQUIRED),
};

const manualSchema = z.object(baseShape);

const buildAutomatedSchema = (companyTimezone: string) =>
	z
		.object({
			...baseShape,
			automationType: z.enum(AUTOMATION_TYPES),
			automationEndDate: z.date().nullable().optional(),
		})
		.superRefine((value, ctx) => {
			if (value.automationType !== "recurring" || !value.automationEndDate) return;

			const tomorrow = startOfDayInDays(1, companyTimezone);

			if (value.automationEndDate < tomorrow) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["automationEndDate"],
					message: "Automation end date must be at least one day in the future",
				});
			} else if (value.startDate && value.automationEndDate < minimumEndDate(value.startDate)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["automationEndDate"],
					message: "Automation end date must be at least one day after the review cycle start date",
				});
			}
		});

export const getEditCycleSchema = (cycleType: CycleType, companyTimezone: string) => {
	switch (cycleType) {
		case "automated":
			return buildAutomatedSchema(companyTimezone);
		case "manual":
		case "group":
			return manualSchema;
	}
};

export type EditCycleFormData = {
	cycleType: CycleType;
	name: string;
	lockReviews: boolean;
	startDate: Date | null;
	automationType?: AutomationType;
	automationEndDate?: Date | null;
};

const isScheduled = (
	autoflow: GetReviewCycleDetailsResponseFrontend["autoflow"]
): autoflow is NonNullable<GetReviewCycleDetailsResponseFrontend["autoflow"]> & {
	trigger: { type: "scheduled"; scheduled: { recurring: boolean; endTimeDate?: Date } };
} => autoflow?.trigger?.type === "scheduled";

export const getEditCycleDefaults = (
	details: GetReviewCycleDetailsResponseFrontend,
	companyTimezone: string
): EditCycleFormData => {
	const base: EditCycleFormData = {
		cycleType: details.type,
		name: details.name,
		lockReviews: details.adminsOnly ?? false,
		startDate: utcToZonedTime(details.startDate, companyTimezone),
	};

	if (details.type !== "automated") return base;

	const recurring = isScheduled(details.autoflow) ? details.autoflow.trigger.scheduled.recurring : false;
	const endTimeDate = isScheduled(details.autoflow) ? details.autoflow.trigger.scheduled.endTimeDate : undefined;

	return {
		...base,
		automationType: recurring ? "recurring" : "oneTime",
		automationEndDate: endTimeDate ? utcToZonedTime(endTimeDate, companyTimezone) : null,
	};
};
